import { z } from 'zod'
import axios from 'axios'
import {
  useMutation,
  useSuspenseQuery,
  useQueryClient,
} from '@tanstack/react-query'

const SettingsSchema = z.object({
  first_name: z.string(),
  last_name: z.string(),
  fields: z.any(),
  otp_enabled: z.boolean(),
  otp_device_name: z.string().nullable(),
  photo: z.record(z.string()).nullable(),
})

export type Settings = z.infer<typeof SettingsSchema>

const SettingsUpdateSchema = z.object({
  first_name: z.string().min(1, 'First name is required').optional(),
  last_name: z.string().min(1, 'Last name is required').optional(),
  photo_id: z.number().optional(),
  fields: z.record(z.union([z.string(), z.number(), z.boolean()])).optional(),
})

export type SettingsUpdate = z.infer<typeof SettingsUpdateSchema>

const ChangePasswordSchema = z
  .object({
    current_password: z.string().min(1, 'Current password is required'),
    new_password: z.string().min(8, 'Password must be at least 8 characters'),
    confirm_password: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords don't match",
    path: ['confirm_password'],
  })

export type ChangePasswordForm = z.infer<typeof ChangePasswordSchema>

async function getSettings() {
  const response = await axios.get('/api/settings')
  return SettingsSchema.parse(response.data)
}

export function useSettings() {
  return useSuspenseQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
  })
}

async function updateSettings(data: SettingsUpdate) {
  const response = await axios.patch('/api/settings', data)
  return response.data
}

export function useUpdateSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })
}

async function changePassword(data: {
  current_password: string
  new_password: string
}) {
  const response = await axios.post('/api/change-password', data)
  return response.data
}

export function useChangePassword() {
  return useMutation({
    mutationFn: changePassword,
  })
}

async function enableOtpGenerate(payload: {
  device_name: string
  password: string
}) {
  const response = await axios.post('/api/enable-otp-generate', {
    device_name: payload.device_name,
    password: payload.password,
  })
  return OtpGenerateSchema.parse(response.data)
}

export function useEnableOtpGenerate() {
  return useMutation({
    mutationFn: enableOtpGenerate,
  })
}

async function enableOtpVerify(payload: { token: string }) {
  const response = await axios.post('/api/enable-otp-verify', {
    token: payload.token,
  })
  return response.data
}

export function useEnableOtpVerify() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: enableOtpVerify,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
    },
  })
}

async function disableTwoFactorAuthentication({ token }: { token: string }) {
  const response = await axios.delete('/api/disable-otp', {
    data: {
      token: token,
    },
  })
  return response.data
}

export function useDisableTwoFactorAuthentication() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: disableTwoFactorAuthentication,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
    },
  })
}

async function getNotificationPreferences() {
  const response = await axios.get('/api/settings/notification-preferences')
  return NotificationPreferencesSchema.parse(response.data)
}

export function useNotificationPreferences() {
  return useSuspenseQuery({
    queryKey: ['notification-preferences'],
    queryFn: getNotificationPreferences,
  })
}

async function updateNotificationPreferences(data: NotificationPreferences) {
  const response = await axios.patch(
    '/api/settings/notification-preferences',
    data
  )
  return response.data
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateNotificationPreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] })
    },
  })
}

export { ChangePasswordSchema, SettingsUpdateSchema }

export const NotificationPreferencesSchema = z.object({
  push_enabled: z.boolean().optional(),
  news: z.boolean().optional(),
  arena_posts: z.boolean().optional(),
})

export type NotificationPreferences = z.infer<
  typeof NotificationPreferencesSchema
>

export const NotificationsChangeSchema = z.object({
  push_enabled: z.boolean().optional(),
  news: z.boolean(),
  arena_posts: z.boolean(),
})

export type NotificationsChange = z.infer<typeof NotificationsChangeSchema>

export const TwoFactorVerificationSchema = z.object({
  device_name: z.string().min(1, 'Device name is required.'),
  password: z.string().min(1, 'Password must not be empty.'),
})

export const OtpGenerateSchema = z.object({
  secret: z.string(),
  url: z.string().url(),
  qr_base64: z.string().base64(),
})

export const TwoFactorAuthenticationSchema = z.object({
  token: z.string(),
})

export type TwoFactorVerification = z.infer<typeof TwoFactorVerificationSchema>
export type TwoFactorAuthentication = z.infer<
  typeof TwoFactorAuthenticationSchema
>
export type Otp = z.infer<typeof OtpGenerateSchema>
